import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronLeft } from "lucide-react";
import { DownloadSimpleIcon } from "@phosphor-icons/react";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import type { TProjectIconColor, TProjectShape } from "@libs/zapi-ui/const";
import { NotificationTrigger } from "@/zapi-frontend/features/Notifications";
import { ProjectIcon } from "@/zapi-frontend/shared/ui/ProjectIconEditor/ProjectIconEditor";
import { HeaderLogo } from "./_HeaderLogo";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { useCaseBriefChatStore } from "@/zapi-frontend/stores/caseBriefChatStore";
import { getRouteProjects } from "@/zapi-frontend/shared/const/router";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";
import { useEffect } from "react";
import { displayVersionName } from "@/zapi-frontend/shared/i18n/versionDisplayName";

const INITIAL_VERSION_NAME = "Initial";

export function CreateProjectHeader() {
  const { t } = useTranslation(["createProject", "projectHome", "common"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const projectName = useCaseBriefChatStore((s) => s.projectName);
  const iconShape = useCaseBriefChatStore((s) => s.iconShape);
  const iconColor = useCaseBriefChatStore((s) => s.iconColor);
  const { isSystemAdmin, availableOrganizations, loadOrganizations, currentOrganizationId } = useOrganizationStore();
  const isTablet = useMediaQuery("(min-width: 720px)");

  useEffect(() => {
    if (isSystemAdmin) loadOrganizations();
  }, [isSystemAdmin, loadOrganizations]);

  const currentOrgName = availableOrganizations.find((org) => org.id === currentOrganizationId)?.name;

  return (
    <>
      <div className="flex w-full justify-between items-center">
        <div className="flex flex-1 items-center gap-3">
          <HeaderLogo />

          {/* Back lives here per design; the content header carries only the page title. */}
          <AppButton
            appearance="text"
            size="icon"
            aria-label="Back to Project List"
            data-testid="create-header-back-button"
            className="shrink-0 -mr-1"
            onClick={() => {
              useCaseBriefChatStore.getState().reset();
              navigate(getRouteProjects());
            }}
          >
            <ChevronLeft className="size-4 text-innoveraZinc-700" />
          </AppButton>

          <div className="flex shrink-0 items-center gap-2">
            <ProjectIcon
              shape={iconShape as TProjectShape | null}
              color={iconColor as TProjectIconColor | null}
              className="size-8 shrink-0"
            />
            <FigmaTypography
              variant="label-2"
              color={projectName ? "zinc900" : "zinc500"}
              className="max-w-[240px] truncate leading-4"
            >
              {projectName || t("newProjectTitle")}
            </FigmaTypography>
            <span className="flex shrink-0 items-center gap-0.5 rounded-md border border-innoveraZinc-150 py-0.5 pl-2 pr-1.5">
              <FigmaTypography component="span" variant="body-2" color="zinc900">
                {displayVersionName(INITIAL_VERSION_NAME)}
              </FigmaTypography>
              <ChevronDown className="size-4 shrink-0 text-innoveraZinc-500" aria-hidden />
            </span>
          </div>

          {isSystemAdmin && currentOrgName && isTablet && (
            <span className="text-innoveraZinc-500 font-normal shrink-0">(org: {currentOrgName})</span>
          )}
        </div>

        <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-1">
          <div className="flex w-fit items-center rounded-[8px] border border-innoveraZinc-150 bg-innoveraZinc-100 p-0.5">
            <span className="flex h-7 items-center rounded-md bg-white px-3">
              <FigmaTypography component="span" variant="label-2" color="zinc900">
                {t("projectHome:header.document")}
              </FigmaTypography>
            </span>
            <span className="flex h-7 items-center rounded-md px-3 opacity-50">
              <FigmaTypography component="span" variant="label-2" color="zinc600">
                {t("projectHome:header.presentation")}
              </FigmaTypography>
            </span>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-end gap-1">
          <AppButton color="zinc100" disabled startAdornment={<DownloadSimpleIcon className="size-4" />}>
            <FigmaTypography variant="label-2" color="zinc400">
              {t("common:exportAs")}
            </FigmaTypography>
          </AppButton>
          <div className="w-[1px] h-3 bg-innoveraZinc-200 ml-2" />
          <NotificationTrigger projectId={id ? parseInt(id) : undefined} />
        </div>
      </div>
      <div className="w-[1px] h-3 bg-innoveraZinc-200 -mx-2" />
    </>
  );
}
